<script setup lang="ts">
import { ref, computed, onBeforeMount, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { PeopleService } from '@/services';
import { useRouting } from '@/composables';
import { useArchiveStore, usePeopleStore, usePeopleTypesStore } from '@/store';
import { usePersonTypes } from '@/composables';

const archiveStore = useArchiveStore();
const peopleStore = usePeopleStore();
const $routing = useRouting();
const toast = useToast();
const reasons = ref();
const reasonId = ref();
const archiveInfo = ref();
const confirmArchiveOpen = ref(false);
const loading = ref(false);
const PEOPLE_TYPES = usePeopleTypesStore().PEOPLE_TYPES;

const emit = defineEmits(['onArchived']);

const rules = computed(() => ({
  reasonId: { required: helpers.withMessage('Please choose a reason', required) },
}));

const v$ = useVuelidate(rules, { reasonId });

const isStudent = computed(() => usePersonTypes.isStudent(archiveStore.person.people_type?.acronym as string));

const needGetInfo = computed(() =>
  usePersonTypes.isType(archiveStore.person.people_type?.acronym as string, [
    PEOPLE_TYPES.STUDENT,
    PEOPLE_TYPES.CLINICIAN,
    PEOPLE_TYPES.PARENT,
    PEOPLE_TYPES.FACILITATOR,
    PEOPLE_TYPES.SCHOOL_ADMIN,
  ]),
);

const formIncomplete = computed(() => isStudent.value && !reasonId.value);

const canArchive = computed(() => !!archiveStore.person.archived_at === false);

onBeforeMount(() => init());

const init = () => {
  loading.value = false;
  confirmArchiveOpen.value = false;
  reasonId.value = false;
  archiveStore.isShow = false;
};

const onCancelArchive = () => init();

watch(archiveStore, (value) => {
  if (value.isShow) openConfirmArchive();
});

const openConfirmArchive = async () => {
  loading.value = true;
  confirmArchiveOpen.value = true;

  if (needGetInfo.value) {
    await PeopleService.getArchiveInfo(archiveStore.person.id).then((response) => {
      archiveInfo.value = response.data.data?.messages;
      reasons.value = response.data.data?.reasons;
    });
  }

  loading.value = false;
};

const onArchive = async () => {
  const payload = reasonId.value ? { archived_reason_id: reasonId.value } : {};
  await PeopleService.archive(archiveStore.person.id, payload)
    .then((response) => doSuccess(response.data.message))
    .catch((error) => doFail(error.data.message));
  confirmArchiveOpen.value = false;
  await peopleStore.getPeople($routing.route.query);
  await peopleStore.getTotals();
  archiveStore.isShow = false;
};

const formatMessage = (message: string, value: string) => {
  return message.replace(':value', `<b>${value}</b>`);
};

const doSuccess = (detail: string) => {
  toast.add({
    severity: 'success',
    summary: 'Archived',
    detail: detail,
    life: 3000,
  });
  emit('onArchived');
};

const doFail = (detail: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: detail,
    life: 3000,
  });
};
</script>

<template>
  <div v-if="canArchive">
    <ConfirmAction
      v-if="confirmArchiveOpen"
      header="Archive User"
      accept-label="Archive User"
      :disable-accept="formIncomplete"
      @accept="onArchive"
      @cancel="onCancelArchive"
    >
      <div class="flex text-gray-800">
        <Icon name="warning" class="confirm-dialog-icon text-gray-600" />
        <div v-if="needGetInfo">
          <span class="w-10 pl-3">This action will:</span>
          <div v-if="loading" style="width: 437px">
            <Skeleton v-for="n in 4" :key="n" width="100%" height="24px" class="mb-2" />
          </div>
          <div v-else>
            <div class="mb-3 mt-3">
              <ol>
                <li v-for="(info, key) in archiveInfo" :key="key" class="mb-1">
                  <!-- eslint-disable vue/no-v-html -->
                  <span v-html="formatMessage(info.message, info.value)"></span>
                  <!--eslint-enable-->
                </li>
              </ol>
            </div>
            <Dropdown
              v-if="isStudent"
              id="reason_id"
              v-model="v$.reasonId.$model"
              class="w-full"
              :options="reasons"
              option-label="reason"
              option-value="id"
              placeholder="Select a reason"
              @blur="v$.reasonId.$touch"
            />
            <div v-for="error of v$.reasonId.$errors" :key="error.$uid" class="input-errors">
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </div>
        </div>
        <div v-else>
          <span class="w-10 pl-3">
            {{ `Are you sure you want to Archive ${archiveStore.person.display_name}?` }}
          </span>
        </div>
      </div>
    </ConfirmAction>
  </div>
</template>
